import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useCart } from '../context/CartContext.js'
import { resolveProductImage } from '../utils/resolveImage'

const tomorrow = () => {
  const d = new Date()
  d.setDate(d.getDate() + 1)
  return d.toISOString().split('T')[0]
}

const support = {
  email: 'info@govurb.co.mz',
  phone: '+258 85 262 0360',
  whatsapp: 'https://wa.me/258852620360',
  horario: 'Seg-Sex 08h-18h',
}
const onImgError = (e) => { e.target.src = '/images/placeholder.svg' }

export default function Checkout() {
  const { cart, clearCart } = useCart()
  const items = Array.isArray(cart) ? cart : []

  const groups = useMemo(() => {
    const m = new Map()
    for (const it of items) {
      const id = it?.id ?? Math.random()
      const entry = m.get(id) || { item: it, quantity: 0 }
      entry.quantity += 1
      m.set(id, entry)
    }
    return Array.from(m.values())
  }, [items])

  const total = useMemo(() => items.reduce((sum, it) => sum + Number(it?.preco || 0), 0), [items])

  const [step, setStep] = useState(1)
  const [metodo, setMetodo] = useState('entrega')
  const [pagamento, setPagamento] = useState('mpesa')
  const [nome, setNome] = useState('')
  const [email, setEmail] = useState('')
  const [telefone, setTelefone] = useState('')
  const [endereco, setEndereco] = useState('')
  const [cidade, setCidade] = useState('')
  const [provincia, setProvincia] = useState('')
  const [distrito, setDistrito] = useState('')
  const [dataEntrega, setDataEntrega] = useState(tomorrow())
  const [observacoes, setObservacoes] = useState('')
  const [aceitoTermos, setAceitoTermos] = useState(false)
  const [processing, setProcessing] = useState(false)
  const [confirmacao, setConfirmacao] = useState(null)

  const navigate = useNavigate()
  const token = typeof window !== 'undefined' ? localStorage.getItem('authToken') : ''

  const validateStep = (s) => {
    if (s === 1) return nome && email && /\S+@\S+\.\S+/.test(email) && telefone
    if (s === 2) {
      if (metodo === 'entrega') return endereco && cidade && provincia && distrito && dataEntrega
      return !!dataEntrega
    }
    if (s === 3) return aceitoTermos
    return true
  }

  const goNext = () => {
    if (validateStep(step)) setStep((p) => Math.min(3, p + 1))
    else alert('Preencha os campos obrigatórios antes de avançar.')
  }
  const goPrev = () => setStep((p) => Math.max(1, p - 1))

  const canReachStep = (target) => {
    if (target <= 1) return true
    if (target === 2) return validateStep(1)
    if (target === 3) return validateStep(1) && validateStep(2)
    return false
  }

  const confirmar = async (e) => {
    e.preventDefault()
    if (!validateStep(3) || items.length === 0) {
      alert('Preencha os campos e valide o carrinho.')
      return
    }
    const codigo = 'PED-' + Date.now().toString(36).toUpperCase().slice(-6)
    const payload = {
      codigo,
      total,
      moeda: 'MZN',
      metodo_pagamento: pagamento,
      status: 'pendente',
      endereco_entrega: metodo === 'entrega' ? endereco : 'Retirar no local',
      provincia,
      distrito,
      data_entrega: dataEntrega,
      observacoes,
      contacto: { nome, email, telefone, cidade },
      itens: groups.map(({ item, quantity }) => ({
        produto_id: item.id,
        nome: item.nome,
        preco: item.preco,
        quantidade: quantity,
        categoria: item.categoria,
        produtor: item.produtor || item.produtor_nome || 'Produtor local',
        localizacao: item.localizacao || 'Moçambique',
        imagem: item.imagem,
      })),
    }

    setProcessing(true)
    let apiOk = false
    try {
      if (token) {
        const res = await fetch('/api/pedidos', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        })
        const data = await res.json().catch(() => ({}))
        if (!res.ok || data.success === false) {
          throw new Error(data.message || 'Falha ao criar pedido')
        }
        apiOk = true
      }
    } catch (err) {
      console.warn('API pedidos falhou, salvando localmente:', err.message)
    }

    try {
      const raw = localStorage.getItem('orders')
      const list = raw ? JSON.parse(raw) : []
      list.push({ ...payload, status: apiOk ? 'confirmado' : 'pendente', createdAt: Date.now() })
      localStorage.setItem('orders', JSON.stringify(list))
    } catch (err) {
      console.error('Falha ao guardar localmente', err)
    }

    console.log('Simular email para', email, 'pedido', codigo)
    console.log('Simular SMS para', telefone, 'pedido', codigo)
    setConfirmacao({ codigo, payload })
    clearCart()
  }

  const steps = ['Dados', 'Entrega', 'Pagamento']

  return (
    <section className="about-section checkout-page">
      <div className="container checkout-container">
        <div className="checkout-header">
          <div className="steps">
            {steps.map((label, idx) => {
              const num = idx + 1
              const active = step === num
              const done = step > num
              return (
                <div
                  key={label}
                  className={`step ${active ? 'active' : ''} ${done ? 'done' : ''}`}
                  onClick={() => { if (canReachStep(num)) setStep(num) }}
                  role="button"
                  tabIndex={0}
                  onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && canReachStep(num)) setStep(num) }}
                >
                  <span className="step-index">{num}</span>
                  <span className="step-label">{label}</span>
                </div>
              )
            })}
          </div>
          <div className="support-box">
            <strong>Precisa de ajuda?</strong>
            <span>Tel: {support.phone}</span>
            <span>Email: {support.email}</span>
            <span>Horário: {support.horario}</span>
            <a className="whatsapp-link" href={support.whatsapp} target="_blank" rel="noreferrer">WhatsApp suporte</a>
          </div>
        </div>

        {items.length === 0 ? (
          <p>Seu carrinho está vazio.</p>
        ) : (
          <div className="checkout-grid">
            <div className="card step-controls">
              <div className="step-controls-left">
                <strong>Passo {step} de 3</strong>
              </div>
              <div className="form-actions">
                {step > 1 && <button type="button" className="ghost-button" onClick={goPrev}>Voltar</button>}
                {step < 3 && <button type="button" className="cta-button secondary" onClick={goNext}>Próximo</button>}
                {step === 3 && (
                  <button type="button" className="cta-button primary" onClick={confirmar} disabled={processing}>
                    {processing ? 'A processar...' : 'Confirmar Encomenda'}
                  </button>
                )}
              </div>
            </div>

            <div className="checkout-form">
              <div className="card">
                <h3>Resumo do Pedido</h3>
                <ul className="order-list">
                  {groups.map(({ item, quantity }) => (
                    <li key={item.id} className="order-item">
                      <img src={resolveProductImage(item)} alt={item.nome} onError={onImgError} />
                      <div className="order-item-info">
                        <div className="order-item-title">{item.nome}</div>
                        <div className="order-item-meta">
                          <span>{item.categoria || 'Categoria'}</span>
                          <span>{item.produtor || item.produtor_nome || 'Produtor local'}</span>
                          <span>{item.localizacao || 'Moçambique'}</span>
                        </div>
                        <div className="order-item-price">
                          {quantity} x {Number(item.preco).toLocaleString('pt-MZ', { style: 'currency', currency: 'MZN' })}
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
                <div className="order-total">
                  <strong>Total: {total.toLocaleString('pt-MZ', { style: 'currency', currency: 'MZN' })}</strong>
                </div>
              </div>

              <form onSubmit={confirmar} className="card form-card">
                {step === 1 && (
                  <div className="form-step">
                    <h3>Dados de contacto</h3>
                    <div className="form-grid">
                      <input placeholder="Nome completo *" value={nome} onChange={(e) => setNome(e.target.value)} required />
                      <input type="email" placeholder="E-mail *" value={email} onChange={(e) => setEmail(e.target.value)} required />
                      <input placeholder="Telefone *" value={telefone} onChange={(e) => setTelefone(e.target.value)} required />
                      <input placeholder="Observações para o produtor" value={observacoes} onChange={(e) => setObservacoes(e.target.value)} />
                    </div>
                  </div>
                )}

                {step === 2 && (
                  <div className="form-step">
                    <h3>Entrega</h3>
                    <div className="radio-row">
                      <label><input type="radio" checked={metodo === 'entrega'} onChange={() => setMetodo('entrega')} /> Entrega</label>
                      <label><input type="radio" checked={metodo === 'retirada'} onChange={() => setMetodo('retirada')} /> Retirar no local</label>
                    </div>
                    {metodo === 'entrega' && (
                      <div className="form-grid">
                        <input placeholder="Endereço *" value={endereco} onChange={(e) => setEndereco(e.target.value)} required />
                        <input placeholder="Cidade *" value={cidade} onChange={(e) => setCidade(e.target.value)} required />
                        <input placeholder="Província *" value={provincia} onChange={(e) => setProvincia(e.target.value)} required />
                        <input placeholder="Distrito *" value={distrito} onChange={(e) => setDistrito(e.target.value)} required />
                      </div>
                    )}
                    <div className="form-grid">
                      <input type="date" min={tomorrow()} value={dataEntrega} onChange={(e) => setDataEntrega(e.target.value)} />
                    </div>
                  </div>
                )}

                {step === 3 && (
                  <div className="form-step">
                    <h3>Pagamento</h3>
                    <div className="radio-col">
                      {[
                        { v: 'mpesa', label: 'Mpesa (Vodacom)' },
                        { v: 'emola', label: 'E-Mola (Tmcel)' },
                        { v: 'conta_movel', label: 'Conta Móvel (Movitel)' },
                        { v: 'card', label: 'Cartão (Visa/Mastercard)' },
                        { v: 'transferencia', label: 'Transferência Bancária' },
                      ].map((opt) => (
                        <label key={opt.v}>
                          <input type="radio" name="pg" value={opt.v} checked={pagamento === opt.v} onChange={() => setPagamento(opt.v)} /> {opt.label}
                        </label>
                      ))}
                    </div>
                    <label className="checkbox-row">
                      <input type="checkbox" checked={aceitoTermos} onChange={(e) => setAceitoTermos(e.target.checked)} />
                      Aceito os termos e condições
                    </label>
                  </div>
                )}

                <div className="form-actions">
                  {step > 1 && <button type="button" className="ghost-button" onClick={goPrev}>Voltar</button>}
                  {step < 3 && <button type="button" className="cta-button secondary" onClick={goNext}>Próximo</button>}
                  {step === 3 && (
                    <button type="submit" className="cta-button primary" disabled={processing}>
                      {processing ? 'A processar...' : 'Confirmar Encomenda'}
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        )}
      </div>

      {confirmacao && (
        <div className="modal-backdrop">
          <div className="modal">
            <h3>Pedido confirmado</h3>
            <p>Número do pedido: <strong>{confirmacao.codigo}</strong></p>
            <p>Enviámos confirmações por email/SMS (simulado).</p>
            <div className="modal-actions">
              <button className="cta-button secondary" onClick={() => window.print()}>Imprimir</button>
              <a className="cta-button secondary" href={support.whatsapp} target="_blank" rel="noreferrer">WhatsApp</a>
              <button className="cta-button primary" onClick={() => { setConfirmacao(null); navigate('/minhas-compras') }}>Ok</button>
            </div>
          </div>
        </div>
      )}
    </section>
  )
}
